/**
 * `/products` — the catalogue: search + category chips over an auto-fill
 * product grid, in either `Browse` or `Tag to event` mode (docs/plan.md,
 * Screens → Catalogue).
 *
 * Offline-first by construction: `useCatalogueProducts` reads the Dexie
 * `catalogue` mirror (lib/catalogue-data.ts), so this screen renders the full
 * catalogue from a cold start with no signal, same as the rest of the app.
 * Filtering (search + chip) is entirely client-side over that array, and is
 * exactly what scopes Tag mode's bulk actions — `filtered` is passed to
 * `Select all shown` / `Clear shown` unchanged.
 *
 * Tag mode itself is NOT offline-capable (docs/plan.md, Entity Behaviour →
 * Event tagging): it is pre-show laptop prep, not a booth action, so it is
 * disabled with an explanation whenever `useIsOffline()` is true — see
 * `CatalogueModeToggle` and `CatalogueTagBar`.
 */
import { createFileRoute, Link, useRouterState } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { isStandingEvent, type ProductWithPhotos } from '@paradise/shared';
import { Printer } from 'lucide-react';
import { z } from 'zod';

import { CatalogueModeToggle, type CatalogueMode } from '@/components/catalogue/catalogue-mode-toggle';
import { CatalogueTagBar } from '@/components/catalogue/catalogue-tag-bar';
import { EventSharePanel } from '@/components/catalogue/event-share-panel';
import { ProductGrid } from '@/components/catalogue/product-grid';
import { useEventTags } from '@/components/catalogue/use-event-tags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSessionStore } from '@/lib/auth/session';
import { useCaptureDraft } from '@/lib/capture-draft';
import {
  ALL_KEY,
  ANY_FINISH_KEY,
  ANY_PRICE_KEY,
  applyFinishFilter,
  applyPriceBandFilter,
  ARCHIVED_KEY,
  presentPriceBands,
  scopeProducts,
  UNCATEGORISED_KEY
} from '@/lib/catalogue-filter';
import {
  nameById,
  useCategories,
  useCatalogueProducts,
  useEventTaggedProductIds,
  useFinishes,
  useMaterials
} from '@/lib/catalogue-data';
import { useIsOffline } from '@/lib/offline';
import { useDebouncedSearchParam } from '@/lib/use-debounced-search-param';
import { useRestoreListScroll } from '@/lib/use-restore-list-scroll';

/** `count` is null for a plain category — only the two derived buckets are worth numbering. */
interface CategoryOption {
  key: string;
  name: string;
  count: number | null;
}

/**
 * Catalogue filter state lives in the URL for the same reason as the Board's
 * (routes/_app/index.tsx): opening a product and coming back must not silently
 * reset the category chip. `cat` carries a category id or one of the sentinels
 * below, `fin` a finish id. `mode` deliberately stays local state — landing
 * back in bulk Tag mode from a URL is not behaviour a URL should have.
 */
const CatalogueSearch = z.object({
  cat: z.string().optional().catch(undefined),
  fin: z.string().optional().catch(undefined),
  // A band LABEL, not an index: the label is what the print sheet and the buyer's
  // share page already speak, and an index would silently re-point if a bracket
  // were ever inserted into `PRICE_BANDS`. Named `band`, not `price`, because the
  // print route this travels to spends `price` on its `PriceMode`.
  band: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined)
});

export const Route = createFileRoute('/_app/products/')({
  // Design's `isCatalog` app bar subtitle — kept from the Wave-1 placeholder.
  // `showEventSelect` puts the global event selector in the app bar (as on the
  // Board); Tag mode tags to whatever real event it points at.
  staticData: { barSubtitle: 'Price List', showEventSelect: true },
  validateSearch: CatalogueSearch,
  component: ProductsScreen
});

// Sentinels and the filter predicate live in lib/catalogue-filter.ts — the
// print route has to reproduce this scope exactly, and a second copy would
// drift into a printed sheet that disagrees with the screen it came from.

function ProductsScreen() {
  const navigate = Route.useNavigate();
  const products = useCatalogueProducts();
  const { data: categories = [] } = useCategories();
  const { data: materials = [] } = useMaterials();
  const { data: finishes = [] } = useFinishes();
  const isOffline = useIsOffline();
  // Tag mode tags to the app bar's global event selection — but only when it is
  // a real show. "All events" (null) and the standing "Direct Enquiries" bucket
  // are never tag targets: Direct Enquiries' picker shows the whole catalogue
  // precisely because it stays untagged, so tagging to it would silently narrow
  // it. Either one leaves tagging disabled with a "pick an event" hint.
  const currentEventId = useSessionStore(state => state.currentEventId);
  const tagEventId = currentEventId !== null && !isStandingEvent(currentEventId) ? currentEventId : undefined;

  const searchParams = Route.useSearch();
  const activeChip = searchParams.cat ?? ALL_KEY;

  const activeFinish = searchParams.fin ?? ANY_FINISH_KEY;

  const activePrice = searchParams.band ?? ANY_PRICE_KEY;

  function setActiveChip(next: string): void {
    void navigate({ search: prev => ({ ...prev, cat: next === ALL_KEY ? undefined : next }), replace: true });
  }

  function setActiveFinish(next: string): void {
    void navigate({ search: prev => ({ ...prev, fin: next === ANY_FINISH_KEY ? undefined : next }), replace: true });
  }

  function setActivePrice(next: string): void {
    void navigate({ search: prev => ({ ...prev, band: next === ANY_PRICE_KEY ? undefined : next }), replace: true });
  }

  const [search, setSearch] = useDebouncedSearchParam(searchParams.q ?? '', next => {
    void navigate({ search: prev => ({ ...prev, q: next === '' ? undefined : next }), replace: true });
  });

  const [mode, setMode] = useState<CatalogueMode>('browse');

  const { taggedIds, isLoading: tagsLoading, isMutating, bulkSet, error: tagError } = useEventTags(tagEventId);

  // Browse mode only: when a real event is selected, narrow the catalogue to the
  // products tagged to that show. Read from the OFFLINE `eventTags` mirror (not
  // the online `useEventTags` fetch) so filtering works at a booth with no
  // signal. Passing `undefined` in Tag mode leaves the full list taggable.
  const browseEventTaggedIds = useEventTaggedProductIds(mode === 'browse' ? tagEventId : undefined);

  const categoryNameById = useMemo(() => nameById(categories), [categories]);
  const materialNameById = useMemo(() => nameById(materials), [materials]);
  const finishNameById = useMemo(() => nameById(finishes), [finishes]);

  const liveProducts = useMemo(() => products.filter(p => p.isActive), [products]);
  const archivedProducts = useMemo(() => products.filter(p => !p.isActive), [products]);

  // A product's categoryId can point at a category the API no longer returns
  // (deactivated) — that reads as "not in the map" and falls back to
  // Uncategorised, exactly like a genuinely blank categoryId.
  const uncategorisedCount = useMemo(
    () => liveProducts.filter(p => !p.categoryId || !categoryNameById.has(p.categoryId)).length,
    [liveProducts, categoryNameById]
  );

  // `Archived` rides in this list rather than sitting apart, exactly as it did
  // as a chip: it is mutually exclusive with every category, and `scopeProducts`
  // reads it as the signal to swap the source list from live to archived.
  const categoryOptions = useMemo<CategoryOption[]>(() => {
    const list: CategoryOption[] = [{ key: ALL_KEY, name: 'All', count: null }];
    for (const category of categories) {
      list.push({ key: category.id, name: category.name, count: null });
    }
    if (uncategorisedCount > 0) {
      list.push({ key: UNCATEGORISED_KEY, name: 'Uncategorised', count: uncategorisedCount });
    }
    if (archivedProducts.length > 0) {
      list.push({ key: ARCHIVED_KEY, name: 'Archived', count: archivedProducts.length });
    }
    return list;
  }, [categories, uncategorisedCount, archivedProducts.length]);

  const categoryNameByKey = useMemo(
    () => new Map(categoryOptions.map(option => [option.key, option.name])),
    [categoryOptions]
  );

  const query = search.trim().toLowerCase();

  // Everything except the finish filter. Kept separate so the finish counts
  // below describe *this* scope — each option then reads as "what you get if
  // you pick it", instead of counting products the chip or search has already
  // excluded.
  const scopedBeforeFinish = useMemo(
    () =>
      scopeProducts(activeChip === ARCHIVED_KEY ? archivedProducts : liveProducts, {
        activeChip,
        query,
        // Browse-by-event narrowing, applied on top of the chip scope.
        restrictToIds: browseEventTaggedIds,
        liveCategoryIds: categoryNameById
      }),
    [activeChip, archivedProducts, liveProducts, categoryNameById, query, browseEventTaggedIds]
  );

  const finishCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of scopedBeforeFinish) {
      for (const finishId of product.finishIds) {
        counts.set(finishId, (counts.get(finishId) ?? 0) + 1);
      }
    }
    return counts;
  }, [scopedBeforeFinish]);

  // Only finishes present in the current scope are offered — plus the selected
  // one even at zero, so the trigger never displays an option the list has
  // dropped.
  const finishOptions = useMemo(
    () => finishes.filter(finish => (finishCounts.get(finish.id) ?? 0) > 0 || finish.id === activeFinish),
    [finishes, finishCounts, activeFinish]
  );

  const afterFinish = useMemo(
    () => applyFinishFilter(scopedBeforeFinish, activeFinish),
    [scopedBeforeFinish, activeFinish]
  );

  // Bands counted after the finish filter but before the price one — same rule
  // as the finish counts above: an option reads as "what you get if you pick it".
  const priceBands = useMemo(() => presentPriceBands(afterFinish, activePrice), [afterFinish, activePrice]);

  const filtered = useMemo(() => applyPriceBandFilter(afterFinish, activePrice), [afterFinish, activePrice]);

  function handleAdd(product: ProductWithPhotos): void {
    useCaptureDraft.getState().addProduct(product);
    // Marks this as an intentional hand-off so `/leads/new` keeps the item
    // instead of treating it as a stray leftover (lib/capture-draft.ts).
    useCaptureDraft.getState().markHandoff();
    void navigate({ to: '/leads/new' });
  }

  // Tag interactions (the checkbox overlay and both bulk actions) need a
  // real event picked, connectivity, and no bulk call already in flight —
  // otherwise a tap would silently no-op against an undefined eventId
  // (see use-event-tags.ts's own guard) with no feedback to the user.
  const tagInteractionsDisabled = isOffline || !tagEventId || isMutating || tagsLoading;

  function handleToggleTag(product: ProductWithPhotos): void {
    bulkSet({ productIds: [product.id], tagged: !taggedIds.has(product.id) });
  }

  function handleSelectAllShown(): void {
    bulkSet({ productIds: filtered.map(product => product.id), tagged: true });
  }

  function handleClearShown(): void {
    bulkSet({ productIds: filtered.map(product => product.id), tagged: false });
  }

  const resetKey = `${mode}|${activeChip}|${activeFinish}|${activePrice}|${query}|${browseEventTaggedIds ? 'event' : 'all'}`;

  // Coming back from a product must land where the user left, not at the top.
  // `products.length > 0` is the "the Dexie mirror has resolved and the grid has
  // height" signal the router's own restoration fires too early to wait for —
  // see lib/use-restore-list-scroll.ts.
  useRestoreListScroll(products.length > 0);

  // Scoped to the history entry so going back restores the window while opening
  // the catalogue fresh still starts at one page (lib/use-incremental-list.ts).
  const historyKey = useRouterState({ select: state => state.location.state.__TSR_key });

  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="sticky top-0 z-20 flex flex-col gap-3 bg-background px-4 pt-4 pb-2">
        <CatalogueModeToggle mode={mode} onChange={setMode} tagModeDisabled={isOffline} />

        {mode === 'tag' && (
          <CatalogueTagBar
            taggedCount={taggedIds.size}
            shownCount={filtered.length}
            onSelectAllShown={handleSelectAllShown}
            onClearShown={handleClearShown}
            disabled={tagInteractionsDisabled}
            isOffline={isOffline}
            noEventSelected={!tagEventId}
            error={tagError}
          />
        )}

        {mode === 'tag' && (
          <EventSharePanel
            eventId={tagEventId}
            isOffline={isOffline}
            filters={{ cat: searchParams.cat, fin: searchParams.fin, band: searchParams.band, q: searchParams.q }}
          />
        )}

        <div className="flex gap-2.5">
          <Input
            placeholder="Search products…"
            value={search}
            onChange={event => setSearch(event.target.value)}
            aria-label="Search products"
            className="flex-1"
          />
          {/* Browse mode already narrows the grid to the event's tagged set, so
              "print what I am looking at" is literal here — the print route's
              tagged-intersection is a no-op rather than a correction. Hidden
              under "All events" and Direct Enquiries: neither is an event with
              a catalogue to print. `EXACT` because these cards carry the list
              price, and the sheet should say what the screen said. */}
          {mode === 'browse' && tagEventId !== undefined && (
            <Button
              type="button"
              variant="outline"
              render={
                <Link
                  to="/products/print/catalogue"
                  search={{
                    event: tagEventId,
                    price: 'EXACT',
                    cat: searchParams.cat,
                    fin: searchParams.fin,
                    band: searchParams.band,
                    q: searchParams.q,
                    cols: 3
                  }}
                  target="_blank"
                  rel="noreferrer"
                />
              }
            >
              <Printer aria-hidden="true" />
              Print
            </Button>
          )}
          {mode === 'browse' && (
            <Button type="button" onClick={() => void navigate({ to: '/products/new' })}>
              + New
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Was a chip row. Folded on 2026-08-20 (owner): with the finish and
              price selects beside it the header had grown to three stacked
              rows, and the chips wrapped to two of them on a tablet. Seven
              categories fit a dropdown comfortably. */}
          <Select value={activeChip} onValueChange={value => setActiveChip(String(value))}>
            <SelectTrigger size="sm" aria-label="Category" className="w-fit">
              <SelectValue>
                {(value: string | null) =>
                  value === null ? 'Category: All' : `Category: ${categoryNameByKey.get(value) ?? 'All'}`
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {categoryOptions.map(option => (
                <SelectItem key={option.key} value={option.key}>
                  {option.count === null ? option.name : `${option.name} · ${String(option.count)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* A select rather than a second chip row: 16 finishes with a long tail
              (six of them tag 4 products or fewer) would scroll forever on a
              phone. The label is rendered from `finishNameById` instead of Base
              UI's `items` map so a restored `?fin=` never flashes a raw id while
              the Dexie mirror is still resolving. */}
          <Select value={activeFinish} onValueChange={value => setActiveFinish(String(value))}>
            <SelectTrigger size="sm" aria-label="Finish" className="w-fit">
              <SelectValue>
                {(value: string | null) =>
                  value === null || value === ANY_FINISH_KEY
                    ? 'Finish: Any'
                    : `Finish: ${finishNameById.get(value) ?? 'Any'}`
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_FINISH_KEY}>Any finish</SelectItem>
              {finishOptions.map(finish => (
                <SelectItem key={finish.id} value={finish.id}>
                  {finish.name} · {finishCounts.get(finish.id) ?? 0}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Shown even at one band, unlike the buyer's copy of this control:
              staff work the same screen all day and a control that comes and
              goes with the data is harder to trust than one that is always
              there. The band label IS the value — see `?price=` above. */}
          <Select value={activePrice} onValueChange={value => setActivePrice(String(value))}>
            <SelectTrigger size="sm" aria-label="Price" className="w-fit">
              <SelectValue>
                {(value: string | null) =>
                  value === null || value === ANY_PRICE_KEY ? 'Price: Any' : `Price: ${value}`
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_PRICE_KEY}>Any price</SelectItem>
              {priceBands.map(label => (
                <SelectItem key={label} value={label}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-4">
        <ProductGrid
          products={filtered}
          materialNameById={materialNameById}
          finishNameById={finishNameById}
          onAdd={handleAdd}
          tag={mode === 'tag' ? { taggedIds, disabled: tagInteractionsDisabled, onToggle: handleToggleTag } : undefined}
          resetKey={resetKey}
          restoreKey={historyKey === undefined ? undefined : `catalogue|${historyKey}`}
        />
      </div>
    </div>
  );
}
